/**
 * OpenThreadsChip — count of open questions / open revision requests on a
 * loop, for list surfaces (BallStrip). Inquiries and revision requests hang
 * off the loop, not an individual ball, so the count is per loop. Refreshes on
 * window focus only (no poll) — a dashboard renders one strip per loop.
 */

import { useMemo } from 'react';
import { HelpCircle, RotateCcw } from 'lucide-react';
import { countOpen } from './threadLogic';
import { useLoopThreads } from './useLoopThreads';

export function OpenThreadsChip({ loopId }: { loopId: string }) {
  const loopIds = useMemo(() => [loopId], [loopId]);
  const { inquiries, revisionRequests } = useLoopThreads(loopIds);
  const questions = countOpen(inquiries);
  const revisions = countOpen(revisionRequests);
  if (questions === 0 && revisions === 0) return null;

  const chip =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]';
  return (
    <span className="inline-flex items-center gap-1.5">
      {questions > 0 && (
        <span className={chip} title="Open questions on this ball">
          <HelpCircle className="h-3 w-3" />
          {questions} open {questions === 1 ? 'question' : 'questions'}
        </span>
      )}
      {revisions > 0 && (
        <span className={chip} title="Open revision requests on this ball">
          <RotateCcw className="h-3 w-3" />
          {revisions} {revisions === 1 ? 'revision request' : 'revision requests'}
        </span>
      )}
    </span>
  );
}
